"use client";

import {memo, useRef} from "react";

import {useVirtualizer} from "@tanstack/react-virtual";

import {useTranslation} from "@/shared/i18n";
import {PhoneData} from "@/shared/models/form/phone";

interface CountryDropdownProps {
    isOpen: boolean;
    search: string;
    filteredPhones: ReadonlyArray<PhoneData>;
    activeCountry: string;
    onSearchChange: (value: string) => void;
    onCountrySelect: (code: string) => void;
}

export const CountryDropdown = memo(({isOpen, search, filteredPhones, activeCountry, onSearchChange, onCountrySelect}: CountryDropdownProps) => {
    const {t} = useTranslation("forms");
    const parentRef = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
        count: filteredPhones.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 48,
        overscan: 5,
    });

    if (!isOpen) {
        return null;
    }

    return (
        <div className="phone-field__dropdown">
            <div className="phone-field__search">
                <input
                    type="text"
                    placeholder={t("searchCountry")}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="phone-field__search-input"
                />
            </div>
            <div ref={parentRef} className="phone-field__country-list">
                <div
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: "100%",
                        position: "relative",
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const phone = filteredPhones[virtualRow.index];
                        const isActive = phone.code === activeCountry;

                        return (
                            <div
                                key={virtualRow.key}
                                onClick={() => onCountrySelect(phone.code)}
                                className={`phone-field__country-item ${isActive ? "active" : ""}`}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                }}
                            >
                                <span className="f32">
                                    <span className={`flag ${phone.code.toLowerCase()}`}></span>
                                </span>
                                <span className="phone-field__country-name">{phone.name}</span>
                                <span className="phone-field__phone-code">+{phone.phone_code}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

CountryDropdown.displayName = "CountryDropdown";
